<template>
  <div class="shipping-container">
    <div>
      <h2>{{ formTitle }}</h2>
      <div>
        <div v-if="showConnectWallet">
          <connect-wallet />
        </div>
        <div v-if="!showConnectWallet && dataLoaded">
          <div v-if="possibleClaims.length > 0" class="claim-instructions">
            <div class="but-wait">To claim your print...</div>
            <ol>
              <li>Select your NFT(s) you wish to claim.</li>
              <li>Enter your name, email, and address.</li>
              <li>You will receive an email with tracking info when your print has shipped.</li>
            </ol>
          </div>
          <div v-else class="claim-instructions">
            <div>
              <p>You do not have any prints to claim in this wallet.</p>
              <a :href="checkClaimsURL()" class="but-wait">I would like to purchase an NFT so I can claim a print</a>
            </div>
          </div>
          <div v-if="step == 'selectTokens'" class="tokens-to-claim">
            
            <div v-for="p in possibleClaims" :key="p.token_id" class="token-to-claim" :class="{'already-claimed': p.alreadyClaimed}" @click="toggleClaimState(p)">
              <img :src="p.preview_image">
              <div class="token-inner">
                <div class="token-title" v-html="p.title_html" />
                <div class="token-description">Token #{{p.token_id}}</div>
                <div class="token-description">Edition #{{p.edition_number}}</div>

                <div style="height: 25px;">
                  <div v-if="p.alreadyClaimed" class="token-being-claimed">
                    <span class="claimed">&#10005;</span> Claimed by
                    <div class="claimed-by-address">{{p.ethAddress}}</div>
                  </div>
                  <button v-if="!p.alreadyClaimed && p.isClaiming" type="submit" class="claim-button claim-inner">Unclaim</button>
                  <button v-if="!p.alreadyClaimed && !p.isClaiming" type="submit" class="claim-button claim-inner highlighted">Claim</button>
                </div>
              </div>
            </div>
            <div style="display: flex; flex-direction: row; gap: 1em; justify-content: flex-end;">
              <button v-if="showSelectAll()" class="buy-now__shared" @click="claimAll">Select All</button>
              <button v-if="claimCount() > 0" class="buy-now__shared highlighted" @click="gotoShipping()">Claim {{ claimCount() }}</button>
            </div>
          </div>
          <div v-if="step == 'showAddressForm'">
            <form @submit.prevent="saveClaim">
              <IconicInput id="shipping_full_name" v-model="shippingInfo.shipping_full_name" type="text" label="Full Name" :autofocus="true" />
              <IconicInput id="email" v-model="shippingInfo.email" type="email" label="Email" />
              <IconicInput id="phone" v-model="shippingInfo.phone" type="phone" label="Phone" />
              <IconicInput id="shipping_address_1" v-model="shippingInfo.shipping_address_1" type="text" label="Address 1" />
              <IconicInput id="shipping_address_2" v-model="shippingInfo.shipping_address_2" type="text" label="Address 2 (optional)" :required="false" />
              <div class="short-input-fields">
                <IconicInput id="shipping_postal_code" v-model="shippingInfo.shipping_postal_code" type="text" label="Postal Code" />
                <IconicInput id="shipping_city" v-model="shippingInfo.shipping_city" type="text" label="City" />
              </div>
              <div class="short-input-fields">
                <IconicInput id="shipping_state" v-model="shippingInfo.shipping_state" type="text" label="State, province, or region" />
                <div class="iconic-country-wrapper">
                  <label :for="id" class="sr-only">Country</label>
                  <select v-model="shippingInfo.shipping_country" required>
                    <option selected disabled value="">Select Country</option>
                    <option v-for="country in countries()" :key="country.code" :value="country">
                      {{ country.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div v-if="deliveryFee()" style="margin-top: .5em; margin-bottom: .5em;">
                <span class="currently-claiming">Delivery Fee:</span> {{ deliveryFee() }}
              </div>
              <div style="margin-bottom: 1em;">
                <div class="currently-claiming">Claiming Token IDs:</div>
                <div>
                  <div v-for="ct in currentlyClaiming()" :key="ct.token_id" style="margin-left: .75em; font-size: 0.9em;">Token #{{ ct.token_id }}</div>
                </div>
              </div>
              <TermsAndConditions />
              <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
              <div style="margin-left: auto">
                <a class="back-button" href="#" @click="gotoSelectTokens">Back</a> &nbsp;
                <input type="submit" class="submit-button" :value="submitButtonText">
              </div>
            </form>
          </div>
          <div v-if="step == 'showOrderComplete'" class="final-ship-info">
            <div class="shipping-title">Order ID:</div>
            <p class="shipping-info"> {{ order.order_id }}</p>
            <div class="shipping-title">Ship to:</div>
            <p class="shipping-info">{{ order.shipping_full_name }}</p>
            <p class="shipping-info">{{ order.email }}</p>
            <p class="shipping-info">{{ order.phone }}</p>
            <p class="shipping-info">{{ order.shipping_address_1 }}</p>
            <p class="shipping-info">{{ order.shipping_address_2 }}</p>
            <p class="shipping-info">{{ order.shipping_postal_code }}</p>
            <p class="shipping-info">{{ order.shipping_city }}</p>
            <p class="shipping-info">{{ order.shipping_state }}</p>
            <p class="shipping-info">{{ order.shipping_country }}</p>
            <div class="shipping-title">Claimed Token IDs:</div>
            <p v-for="ct in claimedTokens" :key="ct" class="shipping-info">#{{ ct }}</p>
            <div>
              <p>Your print{{ ct > 1 ? "s" : "" }} will ship soon.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import IconicInput from '@/components/forms/IconicInput.vue'
import TermsAndConditions from '@/components/TermsAndConditions.vue'
import ConnectWallet from '@/components/pollock/buy/ConnectWallet.vue'

const submitButtonDefaultText = "Submit"
const submitButtonRequestingText = "Verifying..."
const submitButtonRequestingPaymentText = "Burning..."
const submitButtonSubmittingText = "Claiming..."

export default {
  components: {
    IconicInput,
    TermsAndConditions,
    ConnectWallet
  },
  data: () => ({
    submitButtonText: submitButtonDefaultText,
    formTitle: "Claim Your Print",
    step: "selectTokens",
    txnHash: "",
    errorMessage: "",
    shippingInfo: {
      shipping_country: ""
    },
    possibleClaims: [],
    order: { },
    claimed_tokens: []
  }),
  computed: {
    ...mapState({
      address: state => state.auth.account.address,
      showConnectWallet: state => !state.auth.account.isConnected,
      isConnected: state => state.auth.account.isConnected,
      claimMessage: state => state.claim.claimMessage,
      claimInfo: state => state.claim.claimInfo,
      checkingClaim: state => state.claim.checkingClaim,
      availableClaims: state => state.claim.availableClaims,
      dataLoaded: state => state.ui.dataLoaded
    })
  },
  methods: {
    ...mapActions('shipping', ['requestSignature', 'getNonce']),
    ...mapActions('claim', ['getAvailableClaims', 'claimTokens', 'getClaimInfo', 'initiateBurnClaims']),
    ...mapActions('ui', ['showOverlay']),
    ...mapActions('drops', ['checkTransaction']),

    async setupClaimableTokens() {
      const partnerSlug = this.$route.params.partner_slug
      const drops = await this.$store.dispatch('drops/getPartnerDrops', partnerSlug)
      const claimableTokens = await this.getAvailableClaims({partnerSlug, ethAddress: this.address})

      this.getClaimInfo(partnerSlug)
      
      this.possibleClaims = claimableTokens.map(ct => {
        const drop = drops.find(d => d.edition_number == ct.series_number)
        return {
          token_id: ct.token_id,
          title_html: drop.title_html, 
          preview_image: drop.preview_image,
          series_number: ct.series_number,
          edition_number: ct.edition_number,
          alreadyClaimed: !!ct.eth_address,
          ethAddress: ct.eth_address,
          isClaiming: false
        }
      })
      this.$store.dispatch("ui/dataLoaded")
    },
    countries() {
      if(!this.claimInfo && this.claimInfo.countries) {
        return []
      }

      return this.claimInfo.countries
    },
    deliveryFee() {
      if(!this.shippingInfo.shipping_country) {
        return null
      }
      return this.shippingInfo.shipping_country.ethFee
    },
    claimCount() {
      return this.possibleClaims.filter(p => p.isClaiming).length
    },
    currentlyClaiming() {
      return this.possibleClaims.filter(p => p.isClaiming)
    },
    checkClaimsURL() {
      return `/${this.$route.params.partner_slug}/claims/check`
    },
    toggleClaimState(possibleClaim) {
      if(possibleClaim.alreadyClaimed) {
        return
      }
      possibleClaim.isClaiming = !possibleClaim.isClaiming
    },
    claimAll() {
      this.possibleClaims.forEach(possibleClaim => {
        possibleClaim.isClaiming = !possibleClaim.alreadyClaimed
      })
    },
    potentialClaimCount() {
      return this.possibleClaims.filter(p => !p.alreadyClaimed).length
    },
    showSelectAll() {
      return this.potentialClaimCount() > this.claimCount()
    },
    gotoShipping() {
      this.step = 'showAddressForm'
    },
    gotoSelectTokens() {
      this.step = 'selectTokens'
    },
    async saveClaim(e) {
      e.preventDefault()

      const tokenIds = this.possibleClaims
        .filter(pc => pc.isClaiming)
        .map(pc => pc.token_id)

      const shippingInfo = {...this.shippingInfo, shipping_country: this.shippingInfo.shipping_country.code}

      this.errorMessage = ""
      this.submitButtonText = submitButtonRequestingText
      await this.getNonce(this.address)
      const signed_message = await this.requestSignature()

      if(!signed_message) {
        this.errorMessage = "Please verify wallet ownership"
        this.submitButtonText = submitButtonDefaultText
        return
      }

      const contractAddress = this.claimInfo.contract_address
      const fee = this.shippingInfo.shipping_country.fee
      this.submitButtonText = submitButtonRequestingPaymentText
      const { error, hash } = await this.initiateBurnClaims({tokenIds, fee, contractAddress})

      if(error) {
        this.errorMessage = error
        this.submitButtonText = submitButtonDefaultText
        return
      }
      
      this.showOverlay(true)
      const transactionSucceeded = await this.checkTransaction(hash)
      this.showOverlay(false)

      if (!transactionSucceeded) {
        this.errorMessage = "Transaction failed.  Please try again"
        this.submitButtonText = submitButtonDefaultText
        return
      }

      this.submitButtonText = submitButtonSubmittingText
      const claimInfo = {
        signed_message,
        eth_address: this.address,
        ...shippingInfo,
        token_ids: tokenIds
      }
      const partnerSlug = this.$route.params.partner_slug
      const claimResult = await this.claimTokens({partnerSlug, claimInfo})

      if(claimResult.order.order_id) {
        this.order = claimResult.order
        this.formTitle = "Order summary"
        this.step = 'showOrderComplete'
        this.claimedTokens = claimResult.claimed_tokens
      }
      else {
        this.errorMessage = "Transaction failed.  Please contact hello@iconicmoments.co"
      }
      this.submitButtonText = submitButtonDefaultText
    }
  },
  watch: {
    'address'(address) {
      console.log(address)
      if(address) {
        this.setupClaimableTokens()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.back-button {
  font-family: $font-family-primary;
  color: var(--color-primary-one);
}
.currently-claiming {
    margin: .75em 0 .75em 0;
    font-family: $font-family-primary;
    font-size: 0.8em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--color-primary-one);
}
.claim-button {
  font-family: $font-family-secondary;
  background-color: black;
  border: 0.4px solid black;
  border-radius: 3em;
  padding: 0.8em 1.8em;
  color: white;
  width: fit-content;
  cursor: pointer;
  text-decoration: none;
  text-transform: uppercase;

  &.claim-inner {
    margin-top: .2em;
    padding: 0.4em 1.2em;
  }

  &:disabled {
    cursor: not-allowed;
    background-color: rgb(65, 65, 65);
    border: 0.4px solid rgb(65, 65, 65);
  }

  &.highlighted {
    background-color: var(--color-primary-one);
    border: 0.4px solid var(--color-primary-one);
  }
}

.tokens-to-claim {
  margin-top: 1.5em;
  .token-to-claim {
    margin: 1.5em 0 1.5em 0;
    font-family: $font-family-primary;
    display: flex;
    flex-direction: row;
    justify-content: start;
    background-color: var(--color-highlight-one);
    border: 1px solid var(--color-highlight-one);
    padding: 2em;
    border-radius: 6px;
    cursor: pointer;
    gap: 3em;

    > img {
      width: 100px;
      height: 140px;
    }

    &:hover {
      border: 1px solid var(--color-primary-one);
      background-color: $pollock-bg-default;
    }

    &.already-claimed {
      $bg-disabled: #f2f2f2;
      cursor: not-allowed;
      &:hover {
        border: 1px solid $bg-disabled;
        background-color: $bg-disabled;
      }
      background-color: $bg-disabled; 
    }
    
  }
  .token-inner {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: .5em;
    align-items: flex-start;
  }
  .token-title {
    margin-bottom: 0.25em; 
    font-family: 'Poppins';
    font-size: 22px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.36;
    letter-spacing: -0.71px;
    color: #000;
  }
  .token-description {
    font-family: $font-family-primary;
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.42;
    letter-spacing: -0.16px;
    color: #000;
  }
  .token-being-claimed {
    margin-top: .2em;
    font-size: 1.2em;
  }
  .claimed-by-address{
    margin-top: .25rem;
    font-size: .8rem;
    text-overflow: ellipsis;
    max-width: 150px;
    overflow: hidden;

    @include on-tablet {
      max-width: 400px
    }
  }
  .claiming {
    color: #21b521;
  }
  .claimed {
    color: #d60000;
  }
}

.step-container {
  margin: 1em 0;
  @include on-tablet {
    margin: 2em 0;
  }
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  text-align: center;
}
.step-container div {
  font-family: $font-family-primary;
  padding: .5em 0;
  text-transform: uppercase;
  border-bottom: 1px solid #b4b4b4;
  border-left: 1px solid #b4b4b4;
  font-size: 1rem;
  @include on-tablet {
    font-size: 1.2rem;
  }
  &:last-child {
    border-right: 1px solid #b4b4b4;
  }
  &.active {
    border-bottom: 1px solid black;
    border-left: 1px solid black;
    border-right: 1px solid black;
    font-weight: bolder;
  }
}
.final-ship-info {
  margin-top: 1em;
  background-color: #f1f1f1;
  font-family: $font-family-primary;
  font-size: .9em;
  padding: 1em;
}
.shipping-title {
  font-weight: 600;
  margin: .75em 0;
}
.shipping-info {
  margin: 0;
}
.nft-container {
  text-align: center;
  display: none;
  @include on-laptop {
    display: block;
  }
}
form {
  font-family: $font-family-primary;
}
.but-wait, .but-wait a {
  
  margin: 2rem 0 .5em 0;
  font-family: $font-family-secondary;
  font-size: .8em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--color-primary-one);
}
.claim-instructions {
  font-family: $font-family-primary;
  font-size: .9em;
  margin: .25rem 0 .75rem 0;
}
.view-nft, .submit-button {
  display: inline-block;
  color: white;
  background-color: var(--color-primary-one);
  text-transform: uppercase;
  padding: .9em 1.75em;
  border-radius: 25px;
  text-decoration: none;
  font-family: $font-family-secondary;
  font-size: .8em;
}
.submit-button {
  width: 140px;
  background-color: black;
  border:none;
  background-image: none;
  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;
  display: inline-block;
  cursor: pointer;
}
h2 {
  font-family: $font-family-primary;
  font-weight: 600;
  margin-top: 0;

  @include on-laptop {
    font-size: 2.5rem;
  }
}
h3 {
  font-family: $font-family-primary;
  font-weight: 600;
  margin-top: 0;

  @include on-laptop {
    font-size: 2rem;
  }
}
.shipping-container {
  display: flex;
  justify-content: center;
  gap: 2rem;

  @include container-sizer;
  margin-top: 2rem;

  @include on-laptop {
    margin-top: 3rem;
  }

  color: black;
  form {
    display: flex;
    flex-direction: column;
  }
  input[type='submit'] {
    margin-top: 1em;
  }
}
.short-input-fields {
  display: grid;
  grid-template-columns: 1fr;
  align-content: stretch;

  @include on-laptop {
    gap: .5em;
    grid-template-columns: 1fr 1fr;
     &:first-child {
      padding-right: 5em;
    }
  }
}

.iconic-country-wrapper {
  padding: .25em 0;
  select {
    font-family: $font-family-primary;
    width: 100%;
    padding: .25em;
    background-color: #f1f1f1;
    border:none;
    background-image: none;
    -webkit-box-shadow: none;
    -moz-box-shadow: none;
    box-shadow: none;
    display: inline-block;
  }
}

.nft-image {
  max-height: 60vh;
  max-width: 60vw;
}
.error-message {
  margin-top: .5em;
  color: rgb(212, 0, 0);
  padding: .5em 0;
}
</style>
